<template>
  <div class="page page--project">
    <div class="page__header">
      <h1>{{ project.title }}</h1>
    </div>
    <div class="page__content">
      <div class="page__featured">
        <video v-if="toBase(project.video)" autobuffer controls plays-inline :poster="toBase(project.image || placeholderImage)">
          <source :src="toBase(project.video)" :type="'video/' + getExtension(project.video)">
          Your browser does not support the video tag.
        </video>
        <img v-else :src="toBase(project.image || placeholderImage)" :alt="project.title" />
      </div>
      <div class="page__body">
        <div class="page__body__tags">
          <span v-for="category in project.category" :key="category">#{{ category }}</span>
        </div>
        <div class="page__body__description" v-html="project.description"></div>
        <div class="page__body__buttons">
          <button v-if="project.url" @click="open(toBase(project.url))">
            Website
            <font-awesome-icon prefix :icon="['fas', 'globe']"/>
          </button>
          <button v-for="link in project.links" :key="link.name" @click="open(toBase(link.href))">
            {{ link.name }}
            <font-awesome-icon prefix :icon="['fas', 'link']"/>
          </button>
          <button v-for="download in project.downloadables" :key="download.name" @click="open(toBase(download.href))">
            {{ download.name }}
            <font-awesome-icon prefix :icon="['fas', 'download']"/>
          </button>
        </div>
        <div class="page__body__authors">
          <h6>Team</h6>
          <div v-for="participant in project.participants" :key="participant.name" class="author">
            <span class="name">{{ participant.name }}</span> <span v-if="participant.role" class="role">({{ participant.role }})</span>
          </div>
        </div>
        <div class="page__body__meta">
          <div v-if="project.start" class="page__body__meta__start">
            <h6>Start</h6>
            <span class="value">{{ project.start }}</span>
          </div>
          <div v-if="project.end" class="page__body__meta__end">
            <h6>End</h6>
            <span class="value">{{ project.end }}</span>
          </div>
          <div v-if="project.status" class="page__body__meta__status">
            <h6>Status</h6>
            <span class="value">{{ project.status }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'

export default {

  props: {
    id: {
      type: String | Number,
      required: true
    },
    placeholderImage: {
      type: String,
      default: '/static/img/project.png'
    }
  },

  data () {
    return {
      project: {
        title: 'Project Title',
        short: 'Short description about the project',
        description: 'Long html description about the project',
        image: undefined,
        video: undefined,
        participants: [
          { role: 'PI', name: 'John Doe' }
        ],
        start: '2000-01-01',
        end: '2000-01-01',
        category: [],
        status: 'completed',
        links: [],
        downloadables: []
      }
    }
  },

  computed: {
    ...mapGetters(['getProject', 'toBase'])
  },

  mounted () {
    this.fetchProject()
  },

  methods: {
    ...mapMutations(['increase$loading', 'decrease$loading']),

    fetchProject () {
      /* this.increase$loading()
      fetch('/api/projects/' + this.id)
        .then(stream => stream.json())
        .then(data => { this.project = data })
        .then(() => { this.decrease$loading() })
        .catch(error => { this.decrease$loading(); console.error(error) }) */
      this.project = this.getProject(this.id)
    },

    open (href) {
      window.open(href, '_blank')
    },

    getExtension (s) {
      let extension = /(?:\.([^.]+))?$/.exec(s)
      if (extension) {
        extension = extension.slice(1)
      }
      return extension
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.page {
  position: relative;
  min-height: calc(100vh - 8.5rem);

  @media (min-width: 992px) {
    min-height: calc(100vh - 5rem);
  }

  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 200px;
    color: $light-shades;
    z-index: 1000;
    background: $dark-shades;
    padding: 5rem;

    h1 {
      display: block;
      color: $light-shades;
      text-align: center;
      font-size: 3.5rem;
      line-height: 3rem;
    }
  }

  &__content {
    padding: 5rem 1.5rem;

    @media (min-width: 992px) {
      padding: 3rem 3rem;
    }
  }

  &__featured {
    position: relative;
    height: 500px;
    width: 100%;
    overflow: hidden;

    display: flex;
    flex-direction: column;
    justify-content: center;

    img, video {
      display: block;
      height: 100%;
      max-width: 100%;
      flex: 1;
      object-fit: contain;
      transition: opacity 0.3s ease;
    }
  }

  &__body {
    margin: 3rem 0;

    &__tags {
      text-align: right;
      margin: 1rem 0;

      span {
        color: $main;
        font-size: 1rem;
        font-weight: 700;
        margin-right: 1rem;

        &:last-child {
          margin-right: 0;
        }
      }
    }

    &__description {
      margin: 2rem 0;
    }

    &__buttons {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      grid-auto-rows: minmax(min-content, max-content);
      -webkit-columns: 3;
      -moz-columns: 3;
            columns: 3;
      -webkit-column-gap: 0.5rem;
        -moz-column-gap: 0.5rem;
            column-gap: 0.5rem;
      -webkit-row-gap: 0.5rem;
        -moz-row-gap: 0.5rem;
            row-gap: 0.5rem;
      align-items: center;
      justify-items: center;
      margin: 2rem 0;

      button {
        border: none;
        font-family: inherit;
        font-size: inherit;
        color: inherit;
        background: none;
        cursor: pointer;
        padding: 20px 60px;
        display: inline-block;
        margin: 15px 30px;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-weight: 700;
        outline: none;
        position: relative;
        -webkit-transition: all 0.3s;
        -moz-transition: all 0.3s;
        transition: all 0.3s;
        padding: 20px 100px 20px 20px;
        overflow: hidden;
        background-color: $dark-shades;
        color: $light-shades;

        svg, i {
          right: 10px;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          z-index: 5;
        }

        &::after {
          content: '';
          position: absolute;
          z-index: -1;
          -webkit-transition: all 0.3s;
          -moz-transition: all 0.3s;
          transition: all 0.3s;
          width: 70px;
          height: 200%;
          background: rgba(255,255,255,0.1);
          z-index: 1;
          right: 0;
          top: 0;
          margin: -5px 0 0 -5px;
          -webkit-transform-origin: 0 0;
          -webkit-transform: rotate(-20deg);
          -moz-transform-origin: 0 0;
          -moz-transform: rotate(-20deg);
          -ms-transform-origin: 0 0;
          -ms-transform: rotate(-20deg);
          transform-origin: 0 0;
          transform: rotate(-20deg);
        }

        &:hover {
          background-color: $main;
          &::after {
            width: 100px;
          }
        }
      }
    }

    &__meta {
      margin: 3rem 0 0;
      display: flex;
      width: 100%;

      &__start,
      &__end,
      &__status {
        display: flex;
        flex-direction: column;
        flex: 1;
      }
      &__status {
        text-transform: uppercase;
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/pages/Project.vue